import { Navigate, useParams } from 'react-router-dom';
import { isAuthor } from '@/lib/org-roles';
import {
  NewQuizForm,
  QuestionBankDetail,
  QuestionBankForm,
  QuestionBanks,
  QuestionEditor,
  QuizList,
} from '@/components/quiz/authoring';
import { PageHeader } from '@/components/ui';
import { LibraryIndex } from '@/components/library-index';
import { CourseList, NewCourseForm } from '@/pages/course/authoring';
import { EventList, NewEventForm } from '@/components/event/authoring';
import { useOrgContext } from './layout';

/** Authoring pages inside an organization's Library; authors, admins and owners only. */
function useAuthorOrg() {
  const { org, role } = useOrgContext();
  return {
    allowed: isAuthor(role),
    owner: { kind: 'org' as const, orgId: org.id },
    base: `/organizations/${org.id}/library`,
  };
}

/** The org's Library: one card per kind of content, like Nirvik's. */
export function OrgLibraryPage() {
  const { allowed, base } = useAuthorOrg();
  if (!allowed) return <Navigate to=".." replace />;
  return (
    <LibraryIndex
      base={base}
      who="org"
      intro="Your organization’s own content. Members-only by default; make any of it public to share it with everyone."
    />
  );
}

/** Old URLs (`/organizations/:orgId/quizzes/…`) now live under `library/`. */
export function OrgLibraryRedirect({ section }: { section: string }) {
  const { orgId, '*': rest } = useParams();
  return (
    <Navigate to={`/organizations/${orgId}/library/${section}${rest ? `/${rest}` : ''}`} replace />
  );
}

export function OrgQuizzesPage() {
  const { allowed, owner, base } = useAuthorOrg();
  if (!allowed) return <Navigate to=".." replace />;
  return (
    <>
      <PageHeader
        title="Quizzes and assessments"
        description="Self-assessments and challenges built from your question banks."
        back={{ to: base, label: 'Library' }}
      />
      <QuizList owner={owner} base={base} />
    </>
  );
}

export function OrgNewQuizPage() {
  const { allowed, owner, base } = useAuthorOrg();
  if (!allowed) return <Navigate to=".." replace />;
  return <NewQuizForm owner={owner} back={{ to: `${base}/quizzes`, label: 'Quizzes' }} />;
}

export function OrgQuestionBanksPage() {
  const { allowed, owner, base } = useAuthorOrg();
  if (!allowed) return <Navigate to=".." replace />;
  return (
    <>
      <PageHeader
        title="Question banks"
        description="Group questions by topic, technology or sector. Nirvik's library banks are available to reuse."
        back={{ to: base, label: 'Library' }}
      />
      <QuestionBanks owner={owner} base={base} />
    </>
  );
}

export function OrgQuestionBankFormPage() {
  const { allowed, owner, base } = useAuthorOrg();
  if (!allowed) return <Navigate to=".." replace />;
  return <QuestionBankForm owner={owner} base={base} />;
}

export function OrgQuestionBankPage() {
  const { allowed, owner, base } = useAuthorOrg();
  if (!allowed) return <Navigate to=".." replace />;
  return <QuestionBankDetail owner={owner} base={base} />;
}

export function OrgQuestionEditorPage() {
  const { allowed, base } = useAuthorOrg();
  if (!allowed) return <Navigate to=".." replace />;
  return <QuestionEditor base={base} />;
}

export function OrgCoursesPage() {
  const { allowed, owner, base } = useAuthorOrg();
  if (!allowed) return <Navigate to=".." replace />;
  return (
    <>
      <PageHeader
        title="Courses"
        description="Self-paced courses for your members. Assign them from Assignments."
        back={{ to: base, label: 'Library' }}
      />
      <CourseList owner={owner} base={base} />
    </>
  );
}

export function OrgNewCoursePage() {
  const { allowed, owner, base } = useAuthorOrg();
  if (!allowed) return <Navigate to=".." replace />;
  return <NewCourseForm owner={owner} back={{ to: `${base}/courses`, label: 'Courses' }} />;
}

export function OrgEventsPage() {
  const { allowed, owner, base } = useAuthorOrg();
  if (!allowed) return <Navigate to=".." replace />;
  return (
    <>
      <PageHeader
        title="Workshops and webinars"
        description="Live events, scheduled ahead, online, in person or both."
        back={{ to: base, label: 'Library' }}
      />
      <EventList owner={owner} base={base} />
    </>
  );
}

export function OrgNewEventPage() {
  const { allowed, owner, base } = useAuthorOrg();
  if (!allowed) return <Navigate to=".." replace />;
  return <NewEventForm owner={owner} back={{ to: `${base}/events`, label: 'Events' }} />;
}
